// OGSRenderer — the terminal OGS graph node. Lifts geom_uv + color_uv to 3D Gaussians
// and presents them to the scene's SplatMesh (Spark). Side-effect only (no tensor
// output), so it closes the pipeline: encoder → decoder(s) → plücker → renderer.
//
// N SOURCES: a multi-region bundle wires one source per branch (head, teeth, …),
// each with its own lifter + pose verts + port pair (graph/branchPorts.ts). All
// sources are lifted, then presented ONCE. The single-source construction
// ({lifter, updater, getVerts}) is just sources.length === 1. Each source can be
// switched off live (setSourceEnabled — the blueprint branch node's toggle),
// which is how one region gets isolated.
//
// TWO KINDS OF LIFTER, decided at construction and never mixed:
//
//  PACKED (GpuLifter with packedMode — every WebGPU bundle). The words a frame
//  produces make exactly ONE CPU copy and ONE readback for every region:
//   - the updater builds the SplatMesh ONCE at the total slot capacity
//     (ensurePackedTarget) and hands back its own packed array — the array
//     Spark uploads from, so it is the destination, not a staging copy;
//   - the renderer owns ONE MAP_READ buffer laid out at the branches' fixed
//     bases; every branch's submitLift copies its packed output into its slot
//     ON THE GPU, and the frame is one mapAsync + one contiguous copy per
//     active slot into the mesh array, then one commit (commitPacked);
//   - a branch that is off has its slot zeroed, never dropped: the layout, and
//     with it Spark's index→splat map for its async depth sort, is fixed for
//     the session.
//  This replaced a lifter copy, a concat copy, an updater snapshot (a fresh
//  10 MB allocation per frame) and one map per branch; the measurements are
//  in CLAUDE.md ("The packed present is ONE path").
//
//  SoA (the CPU GaussianLifter, and the rig-less multi-region drive that culls
//  over-stretched splats on the SoA fields — OGSModel's drivenLifter): plain
//  lift(), concatGaussians, and the updater's PLY rebuild. Unchanged.
//
// Presents are SERIALIZED here (evaluate queues on `chain`), whoever calls. Every
// caller today paces itself — the head/teeth engine has a mutex, the multipart
// drive's presentFrame runs under its frame slot — but the invariant belongs to
// the owner of the buffer: an overlapped packed present is a WebGPU validation
// error, not a slow frame ("outstanding map pending" / "used in submit while
// mapped"), so the renderer guarantees it rather than trusting each caller to.
//
// The heavy, scene-bound objects (lifters + SplatMeshUpdater, which owns the host
// the splat mesh) are created and owned by OGSModel and injected here — this node
// orchestrates the per-frame lift+present so the graph, not hardcoded OGSModel
// calls, drives the final render. dispose() drops the refs but never tears down
// the injected lifter/updater (the caller outlives the node).

import { nodeTensorToLiftInput } from "../types.ts";
import { concatGaussians } from "./OGSBranch.ts";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";
import type { Gaussians, LiftInput } from "../../inference/lift";
import type { PbrShadeLike } from "../../inference/pbrShadeParams.ts";

export type { PbrShadeLike } from "../../inference/pbrShadeParams.ts";

// Structural slices of GaussianLifter / GpuLifter / SplatMeshUpdater — kept
// minimal so tests inject stubs without the WebGPU / Spark chain.
export interface LifterLike {
  /** SoA path: lift and return the Gaussians (sync on the CPU lifter). */
  lift(verts: Float32Array, geomUv: LiftInput, colorUv: LiftInput): Gaussians | Promise<Gaussians>;
  /** PACKED path (GpuLifter.packedMode). All five present together. */
  packedMode?: boolean;
  submitLift?(geomUv: LiftInput, colorUv: LiftInput, opts?: { skipUvUpload?: boolean; skipReadback?: boolean }): void;
  /** Report the words the renderer already copied into `dest` (and read the
   *  lift's own timestamp span); never maps. */
  finishLift?(dest: Uint32Array): Promise<Gaussians>;
  setSharedReadback?(target: { buffer: GPUBuffer; byteOffset: number } | null): void;
  gpuDevice?: GPUDevice;
  packedByteLength?: number;
  /** A PBR branch's shade stage, recorded inside this lifter's own encoder. */
  pbrShade?: PbrShadeLike | null;
}

export interface SplatUpdaterLike {
  /** The built SplatMesh, for readers that need its world placement.
   *  `updateWorldMatrix` because the SoA path REBUILDS this mesh: a fresh one
   *  carries its placement on position/quaternion/scale but an IDENTITY
   *  matrixWorld until three's next traversal, and reading that leaves the shade
   *  lighting training-frame normals against a world-space probe. */
  splatMesh?: {
    matrixWorld?: { elements: ArrayLike<number> };
    updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void;
    parent?: unknown;
  } | null;
  /** The CPU-lift path: write the concatenated Gaussians into the splat's own
   *  attributes. A packed source never comes here — its lift writes the
   *  renderer's buffers directly. */
  updateGaussians(g: Gaussians): void;
}

/** One branch feeding the renderer: its own lifter + pose verts + port pair. */
export interface OGSRendererSource {
  /** Branch name (head / body) — logs, errors, and the key setSourceEnabled
   *  addresses this source by. */
  name?: string;
  /** Input port pair; see graph/branchPorts.ts for the blueprint-agreed names. */
  ports: [string, string];
  lifter: LifterLike;
  /** Current pose vertices (rig2mesh / frame-scrub output) for THIS branch. */
  getVerts: () => Float32Array;
  /** Packed slot capacity (uv_res²). REQUIRED for a packed source: the per-branch
   *  base offsets must be fixed for the whole session, so a branch that hasn't
   *  rendered yet still has to reserve its range. */
  capacity?: number;
  /** Region toggle (the blueprint branch node's `enabled`), default on. See
   *  setSourceEnabled — a source that is off is skipped whole. */
  enabled?: boolean;
}

export interface OGSRendererOptions {
  id: string;
  // Input port names, default ["geom_uv", "color_uv"]. Single-source only.
  inputs?: [string, string];
  lifter?: LifterLike;
  updater: SplatUpdaterLike;
  /** 'external' is the only value a PACKED source accepts: the lift writes
   *  three's buffers directly and nothing reads back. 'packed':
   *  the lifts are submitted and NOTHING is read back — a renderer on the same
   *  device draws pass 2's buffers directly (render/three). The updater is then
   *  untouched (its mesh stays empty/transparent); `lastCount` reports capacity. */
  present?: "packed" | "external";
  // Current pose vertices (rig2mesh / frame-scrub output) the lift is anchored to.
  getVerts?: () => Float32Array;
  /** Multi-source mode. Mutually exclusive with lifter/getVerts/inputs. */
  sources?: OGSRendererSource[];
}

const WORDS_PER_SPLAT = 4;

/** Walk to the top of the object graph — the scene the lights are in. A splat
 *  mesh sits under the avatar root, which sits under the scene. */
function sceneRootOf(object: unknown): unknown {
  type Parented = { parent?: Parented | null };
  let node = object as Parented | null | undefined;
  while (node?.parent) node = node.parent;
  return node ?? null;
}

type Vec3Like = { x: number; y: number; z: number };

/** What the shade reads off the frame camera. Notes: ~/comments/aos-threejs-poc/OGSRenderer_armShade_camera_frame.md */
type FrameCamera = { position?: Vec3Like; worldPosition?: Vec3Like };

type Active = null | { geom: LiftInput; color: LiftInput };

export class OGSRenderer implements Node {
  readonly id: string;
  readonly type = "ogsRenderer";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[] = [];
  readonly bindings = [];

  private sources: OGSRendererSource[] | null;
  private updater: SplatUpdaterLike | null;
  /** Packed sources (every lifter packedMode) or SoA sources — never mixed. */
  readonly packed: boolean;
  /** Packed: per-source base slot, and the total — fixed for the session. */
  readonly bases: readonly number[];
  readonly capacity: number;
  // Packed: which sources' slots currently hold words, so a branch that is off
  // is zeroed once. Starts true: zeroing an empty slot is harmless.
  private filled: boolean[];
  // Packed: the one MAP_READ buffer every branch lands in, created on the
  // lifters' device the first time a frame presents.
  readonly external: boolean;
  private warnedEmpty = false;
  private warnedRefused = false;
  // Splat count from the most recent present (debug / tests). Packed: the whole
  // capacity when anything rendered (culled slots are transparent), 0 when nothing did.
  lastCount = 0;
  /** Packed: wall time of the frame's single map (its whole readback wait). */
  lastSharedReadbackMs = -1;

  constructor(opts: OGSRendererOptions) {
    this.id = opts.id;
    if (opts.sources) {
      if (opts.sources.length === 0) throw new Error(`OGSRenderer "${opts.id}": sources[] is empty`);
      this.sources = opts.sources.map((s) => ({ ...s }));
    } else {
      const [g, c] = opts.inputs ?? ["geom_uv", "color_uv"];
      if (!opts.lifter || !opts.getVerts) {
        throw new Error(`OGSRenderer "${opts.id}": needs { lifter, getVerts } or sources[]`);
      }
      this.sources = [{ ports: [g, c], lifter: opts.lifter, getVerts: opts.getVerts }];
    }
    this.inputs = this.sources.flatMap((s) => [{ name: s.ports[0] }, { name: s.ports[1] }]);
    this.updater = opts.updater;
    this.external = opts.present === "external";

    const packedCount = this.sources.filter((s) => s.lifter.packedMode === true).length;
    if (packedCount !== 0 && packedCount !== this.sources.length) {
      throw new Error(`OGSRenderer "${opts.id}": mixed packed and SoA lifters across branches — set packedMode uniformly`);
    }
    this.packed = packedCount > 0;
    const bases: number[] = [];
    let total = 0;
    if (this.packed) {
      for (const s of this.sources) {
        const label = s.name ?? s.ports[0];
        if (!(s.capacity! > 0)) throw new Error(`OGSRenderer "${opts.id}": packed source '${label}' needs a capacity (fixed slot offsets)`);
        const l = s.lifter;
        if (typeof l.submitLift !== "function" || !l.gpuDevice) {
          throw new Error(`OGSRenderer "${opts.id}": packed source '${label}' needs a GpuLifter (submitLift)`);
        }
        if ((l.packedByteLength ?? 0) !== s.capacity! * WORDS_PER_SPLAT * 4) {
          throw new Error(`OGSRenderer "${opts.id}": source '${label}' declares ${s.capacity} slots but its lifter packs ${(l.packedByteLength ?? 0) / 16}`);
        }
        bases.push(total);
        total += s.capacity!;
      }
      // A packed source has nowhere else to go. Its lift writes the renderer's
      // own buffers, so without one there is nothing downstream holding splats —
      // and the readback present that used to cover this case wrote into a Spark
      // mesh that no longer exists. Loud at construction rather than a frame that
      // silently draws an empty avatar.
      if (!this.external) {
        throw new Error(`OGSRenderer "${opts.id}": packed sources require present: 'external' — `
          + `their lift writes the renderer's buffers directly and there is no readback present any more`);
      }
    } else if (this.sources.length > 1 && this.sources.some((s) => !s.capacity)) {
      throw new Error(`OGSRenderer "${opts.id}": every source needs a packed capacity when there is more than one (fixed slot offsets)`);
    }
    this.bases = bases;
    this.capacity = total;
    this.filled = this.sources.map(() => true);
  }

  async init(_ctx: NodeContext): Promise<void> {
    // Deps are injected (OGSModel owns the lifters/updater + scene) — nothing to build.
  }

  /** Turn one branch source on/off by name — the blueprint branch node's
   *  `enabled` toggle, applied live. A source that is OFF is skipped before its
   *  inputs are even read: its verts are never requested and its lifter never
   *  runs, so the region costs nothing and its reserved packed slots are simply
   *  cleared (capacity is untouched, so Spark keeps its index→splat map).
   *  Throws on an unknown name — silently ignoring a typo would leave a region
   *  stuck on with the caller believing it toggled. */
  setSourceEnabled(name: string, on: boolean): void {
    if (!this.sources) throw new Error(`OGSRenderer "${this.id}": disposed`);
    const source = this.sources.find((s) => s.name === name);
    if (!source) throw new Error(`OGSRenderer "${this.id}": no source named "${name}"`);
    source.enabled = on;
  }

  private chain: Promise<unknown> = Promise.resolve();

  evaluate(
    frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    const job = this.chain.then(() => this.evaluateNow(frame, inbound));
    this.chain = job.catch(() => { /* keep the queue alive on error */ });
    return job;
  }

  /** Which sources render this frame: on, and both inputs present. A source with
   *  NEITHER input is simply not rendering (the blueprint's region toggle); one
   *  with exactly one is a wiring bug and fails loudly. */
  private activeSources(inbound: Readonly<Record<string, NodeTensor>>): Active[] {
    const sources = this.sources!;
    const single = sources.length === 1;
    return sources.map((s) => {
      // Ahead of the inputs on purpose: a source switched off never has its
      // verts read or its lift submitted, whatever arrived on its pins.
      if (s.enabled === false) return null;
      const geom = inbound[s.ports[0]];
      const color = inbound[s.ports[1]];
      if (!geom || !color) {
        if (geom || color || single) throw new Error(`OGSRenderer "${this.id}": missing ${s.ports[0]} / ${s.ports[1]}`);
        return null;
      }
      return { geom: nodeTensorToLiftInput(geom), color: nodeTensorToLiftInput(color) };
    });
  }

  private async evaluateNow(
    frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    if (!this.sources || !this.updater) throw new Error(`OGSRenderer "${this.id}": disposed`);
    this.frameCamera = frame?.camera ?? null;
    const active = this.activeSources(inbound);
    const any = active.some((a) => !!a);
    if (!any && !this.warnedEmpty) {
      this.warnedEmpty = true;
      // RETURNING here would leave the PREVIOUS frame's splats frozen on screen
      // while onPresented publishes count 0 — the globals and the pixels
      // disagreeing. Present an empty set instead.
      console.info(`[ogs] OGSRenderer "${this.id}": no branch produced UVs this frame — presenting an empty splat set`);
    }
    if (this.packed) await this.presentPacked(active);
    else await this.presentSoA(active);
    if (!any) this.lastCount = 0;
    return {};
  }

  /** This frame's camera, kept for the shade stage — it lights in world space. */
  private frameCamera: FrameCamera | null = null;
  /** Scratch for the placement upload; the shade reads it synchronously. */
  private readonly l2wBasis = new Float32Array(9);
  private readonly l2wTranslation = new Float32Array(3);
  private readonly camScratch = new Float32Array(3);
  private readonly atScratch = new Float32Array(3);

  /** Upload one source's shade frame: where the camera is, and where the splat
   *  sits in the world the light is defined in. A source with no shade stage —
   *  every RGB branch — costs one property read. */
  private armShade(source: OGSRendererSource): void {
    const shade = source.lifter.pbrShade;
    if (!shade) return;
    // WORLD, not `position` — that one is the plücker pass's avatar frame.
    const p = this.frameCamera?.worldPosition ?? this.frameCamera?.position;
    this.camScratch[0] = p?.x ?? 0;
    this.camScratch[1] = p?.y ?? 0;
    this.camScratch[2] = p?.z ?? 0;
    // Column-major elements -> a row-major 3x3 plus the translation. Identity
    // when the mesh is not built yet, which is the frame before anything renders.
    const mesh = this.updater?.splatMesh;
    mesh?.updateWorldMatrix?.(true, false);
    const e = mesh?.matrixWorld?.elements;
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        this.l2wBasis[row * 3 + col] = e ? Number(e[col * 4 + row]) : (row === col ? 1 : 0);
      }
      this.l2wTranslation[row] = e ? Number(e[12 + row]) : 0;
    }
    // The scene root, so the shade can see the lights in it, and where the
    // avatar is standing, because a point light's falloff is a function of the
    // distance to it.
    this.atScratch[0] = this.l2wTranslation[0];
    this.atScratch[1] = this.l2wTranslation[1];
    this.atScratch[2] = this.l2wTranslation[2];
    shade.setFrame(this.camScratch,
                   { basis: this.l2wBasis, translation: this.l2wTranslation },
                   { root: sceneRootOf(mesh), at: this.atScratch });
  }

  private async presentPacked(active: Active[]): Promise<void> {
    // NO READBACK, AND NO ALTERNATIVE TO IT. Every packed branch writes three's
    // four buffers directly (`setThreeTargets`), so a frame is the lifts being
    // SUBMITTED and nothing else; the renderer picks the new splats up on its
    // next frame, and a branch that is off keeps its last splats until it lifts
    // again (its count is its own buffer, so nothing else's order is disturbed).
    //
    // The `packed` present this replaced mapped a shared readback buffer and
    // copied the words into a Spark SplatMesh's own array. It is deleted rather
    // than kept as a fallback because there is no Spark to copy into, and
    // because it was strictly slower than the path that replaced it — the
    // mapAsync alone cost ~3 ms of Chrome GPU-process round trip per frame,
    // whatever the byte count.
    const sources = this.sources!;
    for (let i = 0; i < sources.length; i++) {
      const a = active[i];
      if (!a) continue;
      // THE SHADE FRAME, on this path too. `presentSoA` armed it and this did
      // not, and the gap was invisible while no PBR bundle presented packed: the
      // stage keeps P = 0, writes nothing, and the branch renders pass 2's raw
      // albedo logits — a black character with a few saturated patches, which
      // reads as a broken decode rather than as a missing uniform upload.
      this.armShade(sources[i]);
      sources[i].getVerts();
      sources[i].lifter.submitLift!(a.geom, a.color, { skipReadback: true });
    }
    this.lastSharedReadbackMs = 0;
    this.lastCount = this.capacity;
  }

  private async presentSoA(active: Active[]): Promise<void> {
    const sources = this.sources!;
    const parts: Gaussians[] = [];
    for (let i = 0; i < sources.length; i++) {
      const a = active[i];
      if (!a) continue;
      // Same rule as the packed path: the shade runs inside the lifter's own
      // encoder, so its frame state is uploaded before the lift that records it.
      this.armShade(sources[i]);
      parts.push(await sources[i].lifter.lift(sources[i].getVerts(), a.geom, a.color));
    }
    const g = parts.length === 1 && sources.length === 1 ? parts[0] : concatGaussians(parts);
    this.updater!.updateGaussians(g);
    this.lastCount = g.count;
  }

  dirtyOn(): readonly DirtySource[] {
    // Re-renders whenever its geom/color inputs change — no epoch source of its own.
    return [];
  }

  dispose(): void {
    this.sources = null;
    this.updater = null;
  }
}
